import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild
} from '@angular/core';
import { Location } from '@angular/common';
import {
	ActionList,
	GeneralAction,
	XeroConnectionItem,
	XeroConnections,
	XeroConnectPayload,
	XeroOptionsItem
} from '@models/models';
import { IntegrationsDataProvider } from '@services/integrations/integrations.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { finalize } from 'rxjs/operators';
import { SNModalService } from '@web-services/modal/snModal.service';
import { ConfirmGeneralModalComponent } from '@web-components/common/confirm-general-modal/confirm-general-modal.component';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
	selector: 'integrate-with-xero-v2',
	template: require('./integrate-with-xero-v2-modal.html'),
	styles: [require('./integrate-with-xero-v2-modal.scss')]
})
export class IntegrateWithXeroV2Modal implements OnInit {
	@ViewChild('hiddenOption') hiddenOption;
	@Input() connectionsData: XeroConnections;
	@Output() xeroConnectState: EventEmitter<boolean> = new EventEmitter();

	selectedCompany: XeroConnectionItem;
	enablePaymentsCopying = false;
	revenueAccountCode: string;
	paymentAccountCode: string;
	buttonDisable: boolean;

	constructor(
		private integrationsDataService: IntegrationsDataProvider,
		private messageService: MessageService,
		private modalService: SNModalService,
		public location: Location,
		private activeModal: NgbActiveModal
	) {}

	ngOnInit(): void {
		this.setDefaultValues();
	}

	closeModal(): void {
		this.selectedCompany = null;
		this.revenueAccountCode = null;
		this.paymentAccountCode = null;
		this.activeModal.close(false);
		this.changePathAfterIntegration();
	}

	saveXeroSettings(
		revenueAccountCode: string,
		paymentAccountCode: string
	): void {
		const data: XeroConnectPayload = { revenueAccountCode };
		if (this.enablePaymentsCopying) {
			data.paymentAccountCode = paymentAccountCode;
		}

		if (this.selectedCompany.actions.CONNECT.method === 'ALERT_MODAL') {
			this.showAlert(
				this.selectedCompany.actions.CONNECT.alertMessage.title,
				this.selectedCompany.actions.CONNECT.alertMessage.message
			);
			return;
		}

		if (this.isXeroDataValid(data)) {
			this.buttonDisable = true;
			this.integrationsDataService
				.connectXeroV2(this.selectedCompany.actions.CONNECT.url, data)
				.pipe(finalize((): boolean => (this.buttonDisable = false)))
				.subscribe(
					(): void => {
						this.checkForAdditionalQuestions(this.selectedCompany.actions);
						this.changePathAfterIntegration();
						this.activeModal.close(true);
					},
					(err): void => this.errorConnectXero(err)
				);
		}
	}

	changePathAfterIntegration(): void {
		window.history.replaceState(
			null,
			null,
			window.location.pathname.replace(
				'/integrations/xero/connect/complete',
				'/settings/integrations'
			)
		);
	}

	onOptionChange(newValue: string, placeToAssign: string): void {
		this[placeToAssign] = newValue;
	}

	onCompanyChange(value: string): void {
		[this.selectedCompany] = this.connectionsData.connections.filter(
			(el: XeroConnectionItem): boolean => el.organisationName === value
		);
		if (this.hiddenOption) {
			this.hiddenOption.nativeElement.selected = true;
		}
		this.revenueAccountCode = this.selectCorrectDropdownValue(
			this.selectedCompany.revenueAccounts
		);
		this.paymentAccountCode = this.selectCorrectDropdownValue(
			this.selectedCompany.paymentAccounts
		);
		this.selectedCompany.paymentAccounts.find(
			(el: XeroOptionsItem): boolean =>
				el.selected
					? (this.enablePaymentsCopying = true)
					: (this.enablePaymentsCopying = false)
		);
	}

	toggleRevenueSection(): void {
		this.enablePaymentsCopying = !this.enablePaymentsCopying;
		if (!this.enablePaymentsCopying) {
			this.paymentAccountCode = null;
			this.selectedCompany.paymentAccounts.forEach(
				(item: XeroOptionsItem): boolean => (item.selected = false)
			);
		}
	}

	private setDefaultValues(): void {
		if (this.connectionsData) {
			let orgName = this.connectionsData.connections[0].organisationName;
			this.connectionsData.connections.forEach(
				(el: XeroConnectionItem): void => {
					if (el.connected) {
						orgName = el.organisationName;
					}
				}
			);

			this.onCompanyChange(orgName);
		}
	}

	private selectCorrectDropdownValue(arr: Array<XeroOptionsItem>): string {
		if (arr.length) {
			let val = null;
			arr.forEach((el: XeroOptionsItem): void => {
				if (el.selected) {
					val = el.accountCode;
				}
			});
			return val;
		}

		return null;
	}

	private errorConnectXero({ error }): void {
		const { errorTitle, errorMessage } = error;
		this.messageService.showErrorFromBackend(errorTitle, errorMessage);
	}

	private showAlert(title: string, message: string): void {
		const alertModalRef = this.modalService.openModal(
			ConfirmGeneralModalComponent,
			{
				windowClass: 'ng-bootstrap-modal modal-visible modal-narrow'
			}
		);

		// TODO Remove direct class manipulations after rewriting integrations page to new Angular
		document.body.classList.remove('modal-open');

		alertModalRef.componentInstance.title = title;
		alertModalRef.componentInstance.text = message;
		alertModalRef.componentInstance.buttons = [
			{
				text: 'Got it',
				size: 'lg',
				color: 'default',
				action: 'CLOSE'
			}
		];

		alertModalRef.result.then((): void => {
			document.body.classList.add('modal-open');
		});
	}

	private isXeroDataValid(data: XeroConnectPayload): boolean {
		if (!data.revenueAccountCode) {
			this.messageService.showToaster('e_18', 'xero');
			return false;
		}

		if (this.enablePaymentsCopying && !data.paymentAccountCode) {
			this.messageService.showToaster('e_19', 'xero');
			return false;
		}

		return true;
	}

	private checkForAdditionalQuestions(actions: ActionList): void {
		const action = actions.CONNECT as GeneralAction;

		if (action.withWarningMessage) {
			if (action.withWarningMessage.errorCode === 802) {
				const { title, message } = action.withWarningMessage;
				const modal = this.modalService.openModal(ConfirmGeneralModalComponent);

				modal.componentInstance.title = title;
				modal.componentInstance.text = message;

				modal.componentInstance.buttons = [
					{
						text: 'Cancel',
						color: 'default',
						size: 'lg',
						action: 'CLOSE_ALERT'
					},
					{
						text: 'Continue to Invoice & Payment Settings',
						color: 'success',
						size: 'lg',
						action: 'REDIRECT'
					}
				];
				modal.result.then((data): void => {
					if (data === 'REDIRECT') {
						this.location.go(
							'/settings/payment_schedules/?redirectTo=InvoiceID'
						);
					}
				});
			}
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/settings/integrations-section/modals/integrate-with-xero-v2/integrate-with-xero-v2-modal.ts